/**
 * eSalon Predictive Search
 * Simple search handler that fetches predictive results and injects them into the drawer.
 * Replaces Dawn's PredictiveSearch/SearchForm web components.
 */
(function () {
  let abortController = null;
  let debounceTimer = null;

  function panelOf(input) {
    return input.closest('.esalon-search-drawer__panel');
  }

  // The injected markup carries its result count in a `class="hidden"` span,
  // which is display:none and therefore never announced. Copy it into the
  // drawer's polite live region so screen readers hear the count.
  function announce(input, text) {
    const status = panelOf(input)?.querySelector('[data-predictive-search-status]');
    if (status) status.textContent = text;
  }

  // Drop any previously rendered results and the count that described them, so
  // the drawer and the live region never disagree.
  function clearResults(input, resultsContainer) {
    resultsContainer.innerHTML = '&nbsp;';
    announce(input, '');
  }

  // The drawer snippet renders twice — the mobile header and the desktop header are
  // both in the DOM — and the injected section hardcodes its own ids. Two populated
  // drawers would duplicate those ids, and a tab's aria-controls could then resolve
  // into the other drawer's panel. Keep exactly one drawer populated.
  function clearOtherDrawers(currentContainer) {
    document.querySelectorAll('[data-predictive-search]').forEach(function (container) {
      if (container === currentContainer) return;
      if (container.innerHTML.trim() === '&nbsp;' || container.innerHTML.trim() === '') return;
      container.innerHTML = '&nbsp;';
      const otherStatus = container
        .closest('.esalon-search-drawer__panel')
        ?.querySelector('[data-predictive-search-status]');
      if (otherStatus) otherStatus.textContent = '';
    });
  }

  function init() {
    document.querySelectorAll('.esalon-search-drawer__input').forEach(function (input) {
      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          onSearch(input);
        }, 200);
      });
    });
  }

  function onSearch(input) {
    const term = input.value.trim();
    const resultsContainer = input.closest('.esalon-search-drawer__panel')?.querySelector('[data-predictive-search]');
    if (!resultsContainer) return;

    // Cancel any in-flight request
    if (abortController) {
      abortController.abort();
    }

    if (term === '') {
      clearResults(input, resultsContainer);
      return;
    }

    abortController = new AbortController();

    fetch(
      window.routes.predictive_search_url +
        '?q=' + encodeURIComponent(term) +
        '&resources[limit]=10&resources[limit_scope]=each' +
        '&section_id=esalon-predictive-search',
      { signal: abortController.signal }
    )
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        const div = document.createElement('div');
        div.innerHTML = text;
        const section = div.querySelector('#shopify-section-esalon-predictive-search');
        if (section) {
          clearOtherDrawers(resultsContainer);
          resultsContainer.innerHTML = section.innerHTML;
          const count = resultsContainer.querySelector('[data-predictive-search-live-region-count-value]');
          announce(input, count ? count.textContent.trim().replace(/\s+/g, ' ') : '');
        } else {
          // The response parsed but carried no section wrapper. That means the
          // section_id above no longer matches sections/esalon-predictive-search.liquid.
          // The fetch succeeded, so .catch never runs — say so rather than
          // leaving a silently empty drawer.
          clearResults(input, resultsContainer);
          console.error(
            'Predictive search: no #shopify-section-esalon-predictive-search in the response. ' +
              'The section_id and the section filename have drifted apart.'
          );
        }
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        clearResults(input, resultsContainer);
        const status = panelOf(input)?.querySelector('[data-predictive-search-status]');
        announce(input, status?.dataset.errorText || '');
        console.error('Predictive search error:', error);
      });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
